import { useCallback, useEffect, useRef } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import { useMenu } from 'global/MenuContext';
import { useMenuSelection } from 'global/MenuSelectionContext';
import { useMenuRoutes } from 'hooks/useMenuRoutes';
import { isMeetingRoomExtras } from 'types/menuTypes';
import { extractDocumentId, findMenuPathByDocumentId, findMenuPathByUrl } from 'utils/menuUtils';

interface NavigationState {
  path: string;
  documentId: string;
  level: 'first' | 'second' | 'third';
  timestamp: number;
}

const NAVIGATION_STORAGE_KEY = 'app_navigation_state';
const RECENT_MENUS_KEY = 'recent_menus';

export const useNavigationSync = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { menu } = useMenu();
  const { selectMenuByDocumentId, currentLevel, activeDocumentId } = useMenuSelection();
  const menuRoutes = useMenuRoutes();
  const isNavigating = useRef(false);
  const lastProcessedPath = useRef<string>('');

  // Função para salvar estado de navegação
  const saveNavigationState = useCallback((state: NavigationState) => {
    try {
      localStorage.setItem(NAVIGATION_STORAGE_KEY, JSON.stringify(state));

      // Salva também nos menus recentes
      const recentMenus = JSON.parse(localStorage.getItem(RECENT_MENUS_KEY) || '[]');
      const existingIndex = recentMenus.findIndex((item: any) => item.path === state.path);

      if (existingIndex > -1) {
        recentMenus.splice(existingIndex, 1);
      }

      recentMenus.unshift({
        path: state.path,
        documentId: state.documentId,
        level: state.level,
        timestamp: state.timestamp,
      });

      // Mantém apenas os 10 mais recentes
      if (recentMenus.length > 10) {
        recentMenus.splice(10);
      }

      localStorage.setItem(RECENT_MENUS_KEY, JSON.stringify(recentMenus));
    } catch (error) {
      console.error('[useNavigationSync] Erro ao salvar estado:', error);
    }
  }, []);

  // Função para carregar estado de navegação
  const loadNavigationState = useCallback((): NavigationState | null => {
    try {
      const stored = localStorage.getItem(NAVIGATION_STORAGE_KEY);
      if (!stored) return null;

      const state: NavigationState = JSON.parse(stored);
      const now = Date.now();

      // Verifica se o estado não é muito antigo (5 minutos)
      if (now - state.timestamp > 5 * 60 * 1000) {
        localStorage.removeItem(NAVIGATION_STORAGE_KEY);
        return null;
      }

      return state;
    } catch (error) {
      console.error('[useNavigationSync] Erro ao carregar estado:', error);
      localStorage.removeItem(NAVIGATION_STORAGE_KEY);
      return null;
    }
  }, []);

  // Função para encontrar rota de fallback inteligente
  const findSmartFallbackLocal = useCallback(() => {
    const currentPath = window.location.pathname;

    console.log('[useNavigationSync] Procurando fallback inteligente para:', currentPath);

    // 1. Tenta encontrar menu similar ao atual
    const currentMenuPath = findMenuPathByUrl(menu, currentPath);
    if (currentMenuPath?.items.length) {
      const parentMenu = currentMenuPath.items[0];
      if (parentMenu.subMenu?.length) {
        const firstSubMenu = parentMenu.subMenu[0];
        const subMenuPath = findMenuPathByDocumentId(
          menu,
          firstSubMenu.feature?.documentId ||
            (firstSubMenu.extras && isMeetingRoomExtras(firstSubMenu.extras)
              ? firstSubMenu.extras.internal_id
              : '') ||
            '',
          firstSubMenu.path
        );
        if (subMenuPath) {
          console.log('[useNavigationSync] Fallback 1: Submenu do menu atual:', subMenuPath.path);
          return {
            path: subMenuPath.path,
            documentId: subMenuPath.documentId,
            reason: 'submenu_atual',
          };
        }
      }
    }

    // 2. Tenta encontrar menu ativo recentemente usado
    try {
      const recentMenus = JSON.parse(localStorage.getItem(RECENT_MENUS_KEY) || '[]');
      if (recentMenus.length > 0) {
        for (const recentMenu of recentMenus) {
          const menuPath = findMenuPathByUrl(menu, recentMenu.path);
          if (menuPath?.items.length) {
            console.log('[useNavigationSync] Fallback 2: Menu recente:', recentMenu.path);
            return {
              path: recentMenu.path,
              documentId: recentMenu.documentId,
              reason: 'menu_recente',
            };
          }
        }
      }
    } catch (error) {
      console.error('[useNavigationSync] Erro ao processar menus recentes:', error);
    }

    // 3. Tenta encontrar menu ativo no mesmo nível
    const currentLevelNumber = currentLevel === 'first' ? 1 : currentLevel === 'second' ? 2 : 3;
    const menusAtSameLevel = menu.filter(item => {
      if (currentLevelNumber === 1) return item.level === 'first' && item.isActive;
      if (currentLevelNumber === 2) return item.level === 'second' && item.isActive;
      return item.level === 'third' && item.isActive;
    });

    if (menusAtSameLevel.length > 0) {
      const firstMenu = menusAtSameLevel[0];
      const menuPath = findMenuPathByDocumentId(
        menu,
        firstMenu.feature?.documentId ||
          (firstMenu.extras && isMeetingRoomExtras(firstMenu.extras)
            ? firstMenu.extras.internal_id
            : '') ||
          ''
      );
      if (menuPath) {
        console.log('[useNavigationSync] Fallback 3: Menu no mesmo nível:', menuPath.path);
        return {
          path: menuPath.path,
          documentId: menuPath.documentId,
          reason: 'mesmo_nivel',
        };
      }
    }

    // 4. Último recurso: primeiro menu ativo
    const firstActiveMenu = menu.find(item => item.isActive);
    if (firstActiveMenu?.subMenu?.length) {
      const firstSubMenu = firstActiveMenu.subMenu[0];
      const menuPath = findMenuPathByDocumentId(
        menu,
        firstSubMenu.feature?.documentId ||
          (firstSubMenu.extras && isMeetingRoomExtras(firstSubMenu.extras)
            ? firstSubMenu.extras.internal_id
            : '') ||
          ''
      );
      if (menuPath) {
        console.log('[useNavigationSync] Fallback 4: Primeiro menu ativo:', menuPath.path);
        return {
          path: menuPath.path,
          documentId: menuPath.documentId,
          reason: 'primeiro_ativo',
        };
      }
    }

    console.warn('[useNavigationSync] Nenhum fallback encontrado');
    return null;
  }, [findMenuPathByUrl, menu, currentLevel]);

  // Função para navegar de forma robusta
  const navigateToPath = useCallback(
    (path: string, documentId?: string) => {
      if (isNavigating.current) return;

      isNavigating.current = true;

      try {
        console.log('[useNavigationSync] Navegando para:', { path, documentId });

        // Se temos documentId, usa selectMenuByDocumentId
        if (documentId) {
          selectMenuByDocumentId(documentId, true, undefined, path);
        } else {
          // Caso contrário, apenas navega
          navigate(path);
        }

        // Salva o estado de navegação
        const menuPath = findMenuPathByUrl(menu, path);
        if (menuPath?.items.length) {
          const lastMenu = menuPath.items[menuPath.items.length - 1];
          const level = menuPath.level;

          saveNavigationState({
            path,
            documentId: documentId || menuPath.documentId,
            level,
            timestamp: Date.now(),
          });
        }
      } catch (error) {
        console.error('[useNavigationSync] Erro na navegação:', error);
      } finally {
        // Reseta o flag após um pequeno delay
        setTimeout(() => {
          isNavigating.current = false;
        }, 100);
      }
    },
    [navigate, selectMenuByDocumentId, findMenuPathByUrl, saveNavigationState, menu]
  );

  // Função para restaurar navegação
  const restoreNavigation = useCallback(() => {
    const savedState = loadNavigationState();
    if (!savedState) return false;

    // Verifica se a rota ainda é válida
    const isValidRoute = menuRoutes.some(route => route.path === savedState.path);
    if (!isValidRoute) {
      console.log('[useNavigationSync] Rota salva não é mais válida:', savedState.path);
      localStorage.removeItem(NAVIGATION_STORAGE_KEY);
      return false;
    }

    console.log('[useNavigationSync] Restaurando navegação:', savedState);
    navigateToPath(savedState.path, savedState.documentId);
    return true;
  }, [loadNavigationState, menuRoutes, navigateToPath]);

  // Sincronização automática baseada na URL
  useEffect(() => {
    if (isNavigating.current || location.pathname === lastProcessedPath.current) {
      return;
    }

    const currentPath = location.pathname;
    lastProcessedPath.current = currentPath;

    // Se estamos em uma rota pública, não sincroniza
    const publicRoutes = ['/login', '/register', '/forgot-password', '/register-iframe'];
    if (publicRoutes.includes(currentPath)) {
      return;
    }

    // Encontra o menu correspondente ao path atual
    const menuPath = findMenuPathByUrl(menu, currentPath);
    if (!menuPath?.items.length) {
      console.log('[useNavigationSync] Menu não encontrado para path:', currentPath);
      return;
    }

    const lastMenu = menuPath.items[menuPath.items.length - 1];
    const documentId = extractDocumentId(lastMenu);

    if (documentId) {
      console.log('[useNavigationSync] Sincronizando com menu:', {
        path: currentPath,
        documentId,
        level: menuPath.level,
      });

      // Usa selectMenuByDocumentId sem navegar (já estamos na URL correta)
      selectMenuByDocumentId(documentId, false, menuPath.items, currentPath);
    }
  }, [location.pathname, findMenuPathByUrl, selectMenuByDocumentId, menu]);

  // Restaura navegação quando o menu é carregado
  useEffect(() => {
    if (menu.length > 0 && !activeDocumentId) {
      const currentPath = window.location.pathname;

      // Exclude event-invite paths from automatic navigation
      const isEventInvitePath = currentPath.startsWith('/invite/event/');
      if (isEventInvitePath) {
        console.log('[useNavigationSync] Event-invite path detected, skipping auto-navigation');
        return;
      }

      // PRIMEIRO: Verifica se a rota atual é válida e encontrada no menu
      const currentMenuPath = findMenuPathByUrl(menu, currentPath);
      if (currentMenuPath?.items.length) {
        console.log(
          '[useNavigationSync] Rota atual encontrada no menu, aguardando sincronização automática'
        );
        return;
      }

      // SEGUNDO: Verifica se a rota atual é válida no menuRoutes
      const isCurrentRouteValid =
        menuRoutes.length > 0 && menuRoutes.some(route => route.path === currentPath);
      if (isCurrentRouteValid) {
        console.log('[useNavigationSync] Rota atual é válida, aguardando sincronização automática');
        return;
      }

      // TERCEIRO: Tenta restaurar do localStorage
      const restored = restoreNavigation();
      if (!restored) {
        // QUARTO: Se não conseguiu restaurar, usa fallback inteligente
        const fallback = findSmartFallbackLocal();
        if (fallback) {
          console.log('[useNavigationSync] Usando fallback inteligente:', fallback);
          navigateToPath(fallback.path, fallback.documentId);
        } else {
          console.warn('[useNavigationSync] Nenhum fallback disponível, mantendo estado atual');
        }
      }
    }
  }, [
    menu.length,
    activeDocumentId,
    restoreNavigation,
    findSmartFallbackLocal,
    navigateToPath,
    menuRoutes,
    findMenuPathByUrl,
  ]);

  return {
    navigateToPath,
    restoreNavigation,
    findMenuPathByUrl: (path: string) => findMenuPathByUrl(menu, path),
    findSmartFallback: findSmartFallbackLocal,
  };
};
